/**
 * Configuración de Microsoft Authentication Library (MSAL) para Azure AD
 * 
 * Antes de usar, configura las variables de entorno:
 * - REACT_APP_DISABLE_AUTH: "true" para deshabilitar autenticación (solo desarrollo local)
 * - REACT_APP_AZURE_CLIENT_ID: ID de la aplicación registrada en Azure AD
 * - REACT_APP_AZURE_TENANT_ID: ID del tenant de Azure AD
 * - REACT_APP_AZURE_REDIRECT_URI: URI de redirección (opcional, usa window.location.origin por defecto)
 */

import { Configuration, LogLevel, PopupRequest, RedirectRequest } from '@azure/msal-browser';

/**
 * Verifica si la autenticación está deshabilitada
 * Solo debe usarse en desarrollo local
 */
export const isAuthDisabled = (): boolean => {
  const disableAuth = process.env.REACT_APP_DISABLE_AUTH?.toLowerCase();
  return disableAuth === 'true';
};

/**
 * Usuario de desarrollo (solo cuando la autenticación está deshabilitada).
 * En modo dev se usa sopp@gft.com para que automáticamente tenga rol superadmin.
 */
export const mockDevUser = {
  id: 'dev-user-local',
  name: 'Super Admin (Dev)',
  email: 'sopp@gft.com',
  username: 'sopp@gft.com',
  roles: ['App.Admin', 'App.User', 'Superadmin'],
};

/** Email(s) considerados superadmin en el frontend */
export const SUPERADMIN_EMAILS = ['sopp@gft.com'];

/**
 * Configuración de MSAL para Azure AD
 * Documentación: https://docs.microsoft.com/azure/active-directory/develop/msal-js-initializing-client-applications
 */
export const msalConfig: Configuration = {
  auth: {
    // ID de la aplicación registrada en Azure Portal
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || 'YOUR_CLIENT_ID',
    
    // Autoridad: Puede ser un tenant específico o 'common' para multi-tenant
    // Para single-tenant: https://login.microsoftonline.com/{tenant-id}
    // Para multi-tenant: https://login.microsoftonline.com/common
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    
    // URI de redirección después del login
    redirectUri: process.env.REACT_APP_AZURE_REDIRECT_URI || window.location.origin,
    
    // URI de redirección después del logout
    postLogoutRedirectUri: window.location.origin,
    
    // Permitir navegación a dominios externos durante el flujo de autenticación
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Donde almacenar los tokens: 'sessionStorage' o 'localStorage'
    // sessionStorage es más seguro pero requiere re-login al cerrar el navegador
    cacheLocation: 'sessionStorage',
    
    // Evitar que terceros accedan a los tokens en iframes
    storeAuthStateInCookie: false,
  },
  system: {
    // Nivel de log para debugging
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          case LogLevel.Info:
            console.info('[MSAL]', message);
            break;
          case LogLevel.Verbose:
            console.debug('[MSAL]', message);
            break;
          default:
            break;
        }
      },
      logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Verbose : LogLevel.Error,
      piiLoggingEnabled: false,
    },
    // Tiempo de espera para redirecciones
    windowHashTimeout: 60000,
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0,
  },
};

/**
 * Scopes para la solicitud de login
 * - openid: Obtiene el ID token
 * - profile: Obtiene información básica del perfil
 * - email: Obtiene el email del usuario
 * - User.Read: Permite leer el perfil del usuario desde Microsoft Graph
 */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Configuración para login mediante redirect (alternativa al popup)
 */
export const loginRedirectRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes para acceder a Microsoft Graph API (opcional)
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphPhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
};

/**
 * Scopes adicionales para APIs personalizadas (si las tienes)
 * Configura esto si tu backend requiere tokens específicos
 */
export const apiConfig = {
  // Si tienes una API registrada en Azure AD, configura su scope aquí
  // Ejemplo: 'api://{your-api-client-id}/access_as_user'
  scopes: process.env.REACT_APP_API_SCOPE 
    ? [process.env.REACT_APP_API_SCOPE] 
    : [],
};

/**
 * Roles de la aplicación (si usas RBAC en Azure AD)
 * Estos deben coincidir con los roles configurados en el App Registration
 */
export const AppRoles = {
  Admin: 'App.Admin',
  User: 'App.User',
  ReadOnly: 'App.ReadOnly',
} as const;

export type AppRole = typeof AppRoles[keyof typeof AppRoles];
